import {DestroyRef, inject, Injectable, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
} from '@angular/router';
import {filter} from 'rxjs';

/** Progress is stored as 0..1, matching NProgress. */
const MINIMUM = 0.08;
const SHOW_DELAY_MS = 120;
const TRICKLE_SPEED_MS = 200;
const SPEED_MS = 200;

@Injectable({
  providedIn: 'root',
})
export class NavigationProgressService {
  /** 0..1 while running; 0 when idle/hidden. */
  readonly status = signal(0);
  readonly visible = signal(false);
  readonly finishing = signal(false);

  private showTimer: ReturnType<typeof setTimeout> | null = null;
  private trickleTimer: ReturnType<typeof setTimeout> | null = null;
  private completeTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    this.router.events
      .pipe(
        filter(
          (event) =>
            event instanceof NavigationStart ||
            event instanceof NavigationEnd ||
            event instanceof NavigationCancel ||
            event instanceof NavigationError,
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event) => {
        if (event instanceof NavigationStart) {
          this.scheduleStart();
          return;
        }
        this.done();
      });
  }

  /** Bar translateX percent: -100%..0% for status 0..1 */
  barTranslatePercent(): number {
    return (-1 + this.status()) * 100;
  }

  private scheduleStart(): void {
    this.clearTimers();
    this.finishing.set(false);
    this.showTimer = setTimeout(() => {
      this.start();
      this.showTimer = null;
    }, SHOW_DELAY_MS);
  }

  private start(): void {
    if (!this.visible()) {
      this.visible.set(true);
    }
    this.finishing.set(false);
    this.set(MINIMUM);
    this.work();
  }

  private work(): void {
    this.clearTrickle();
    this.trickleTimer = setTimeout(() => {
      if (!this.visible() || this.finishing()) {
        return;
      }
      this.trickle();
      this.work();
    }, TRICKLE_SPEED_MS);
  }

  private trickle(): void {
    this.inc();
  }

  /** NProgress.inc: larger steps early, tiny steps near the end, never auto-complete. */
  private inc(amount?: number): void {
    let n = this.status();
    if (!this.visible()) {
      this.start();
      return;
    }
    if (n > 1) {
      return;
    }

    let step = amount;
    if (typeof step !== 'number') {
      if (n >= 0 && n < 0.2) {
        step = 0.1;
      } else if (n >= 0.2 && n < 0.5) {
        step = 0.04;
      } else if (n >= 0.5 && n < 0.8) {
        step = 0.02;
      } else if (n >= 0.8 && n < 0.99) {
        step = 0.005;
      } else {
        step = 0;
      }
      // Slight randomness like ngx-loading-bar for organic feel.
      step *= 0.7 + Math.random() * 0.6;
    }

    this.set(clamp(n + step, 0, 0.994));
  }

  private set(n: number): void {
    this.status.set(clamp(n, MINIMUM, 1));
  }

  /** NProgress.done: placebo bump then finish to 1 and fade out. */
  private done(): void {
    this.clearShowTimer();
    this.clearTrickle();

    if (!this.visible()) {
      this.reset();
      return;
    }

    this.inc(0.3 + 0.5 * Math.random());
    this.status.set(1);

    this.clearCompleteTimer();
    // Hold at 100% for one transition tick, then fade (NProgress timing).
    this.completeTimer = setTimeout(() => {
      this.finishing.set(true);
      this.completeTimer = setTimeout(() => {
        this.reset();
        this.completeTimer = null;
      }, SPEED_MS);
    }, SPEED_MS);
  }

  private reset(): void {
    this.visible.set(false);
    this.finishing.set(false);
    this.status.set(0);
  }

  private clearTimers(): void {
    this.clearShowTimer();
    this.clearTrickle();
    this.clearCompleteTimer();
  }

  private clearShowTimer(): void {
    if (this.showTimer != null) {
      clearTimeout(this.showTimer);
      this.showTimer = null;
    }
  }

  private clearTrickle(): void {
    if (this.trickleTimer != null) {
      clearTimeout(this.trickleTimer);
      this.trickleTimer = null;
    }
  }

  private clearCompleteTimer(): void {
    if (this.completeTimer != null) {
      clearTimeout(this.completeTimer);
      this.completeTimer = null;
    }
  }
}

function clamp(n: number, min: number, max: number): number {
  if (n < min) {
    return min;
  }
  if (n > max) {
    return max;
  }
  return n;
}
